/**
 * RecorderPage — full-screen record-and-generate.
 *
 * Opened in its own tab so the live browser gets the whole viewport (the modal
 * was far too cramped to actually drive a flow). You drive the browser on the
 * left, captured steps stream on the right, and "Generate test" hands the steps
 * back to the QA Agent tab (via postMessage) to write the test.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AppBar, Toolbar, Box, Button, Chip, CircularProgress, TextField,
  Typography, Alert,
} from '@mui/material';
import {
  FiberManualRecord as RecordIcon, Stop as StopIcon, PlayArrow as RunIcon,
  Videocam as VideocamIcon, Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  startRecording, getRecorderEvents, stopRecording, closeRecording,
  recorderNavigate, RecordedStep,
} from '../services/project.service';

type Phase = 'idle' | 'starting' | 'recording' | 'stopped' | 'ended';

const fmt = (s: number) => {
  const m = Math.floor(s / 60), sec = s % 60;
  return `${m}:${sec < 10 ? '0' : ''}${sec}`;
};

const stepLabel = (s: RecordedStep): string => {
  if (s.action === 'navigate') return `go to ${s.url}`;
  if (s.action === 'type') return `type "${s.value}" into ${s.selector}`;
  if (s.action === 'click') return `click ${s.text ? `"${s.text}"` : ''} ${s.selector}`;
  if (s.action === 'submit') return `submit ${s.selector}`;
  return s.action;
};

const RecorderPage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const projectId = params.get('projectId');

  const [phase, setPhase] = useState<Phase>('idle');
  const [startUrl, setStartUrl] = useState(params.get('url') || '');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [vncUrl, setVncUrl] = useState<string | null>(null);
  const [steps, setSteps] = useState<RecordedStep[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [handedOff, setHandedOff] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [remaining, setRemaining] = useState<number | null>(null);
  const pollRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  const stopPoll = () => {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
    if (tickRef.current) { window.clearInterval(tickRef.current); tickRef.current = null; }
  };

  useEffect(() => () => { if (sessionId) closeRecording(sessionId); stopPoll(); }, [sessionId]);

  const handleStart = async () => {
    if (!startUrl.trim()) return;
    setPhase('starting'); setError(null); setNotice(null); setSteps([]);
    try {
      const { session_id, vnc_url, page_loaded, expires_in_seconds } = await startRecording(startUrl.trim(), projectId);
      setSessionId(session_id); setVncUrl(vnc_url); setPhase('recording');
      setRemaining(expires_in_seconds ?? null);
      if (page_loaded === false) {
        setNotice('The page was slow to load. If the browser looks blank, click Reload.');
      }
      pollRef.current = window.setInterval(async () => {
        try {
          const r = await getRecorderEvents(session_id);
          setSteps(r.steps);
          if (typeof r.expires_in_seconds === 'number') setRemaining(r.expires_in_seconds);
          if (r.alive === false) {
            stopPoll();
            setPhase((p) => (p === 'recording' ? 'ended' : p));
          }
        } catch { /* transient */ }
      }, 1500);
      // Local 1s countdown between server syncs.
      tickRef.current = window.setInterval(() => {
        setRemaining((s) => (s === null ? s : Math.max(0, s - 1)));
      }, 1000);
    } catch (e: any) { setError(e.message); setPhase('idle'); }
  };

  const handleReload = async () => {
    if (!sessionId) return;
    setReloading(true); setError(null);
    try { await recorderNavigate(sessionId, startUrl.trim()); }
    catch (e: any) { setError(e.message); }
    finally { setReloading(false); }
  };

  const recordAgain = () => {
    if (sessionId) closeRecording(sessionId);
    stopPoll();
    setPhase('idle'); setSessionId(null); setVncUrl(null); setSteps([]);
    setError(null); setNotice(null); setHandedOff(false); setRemaining(null);
  };

  const handleStop = async () => {
    if (!sessionId) return;
    stopPoll();
    try {
      setSteps((await stopRecording(sessionId)).steps);
      setPhase('stopped');
    } catch {
      // Even if the final drain fails, don't strand the user — keep whatever
      // steps we already polled and let them Generate or Record again.
      setPhase('stopped');
      setNotice('Could not reach the browser session for a final read; using the steps captured so far.');
    }
  };

  const handleGenerate = useCallback(() => {
    if (sessionId) closeRecording(sessionId);
    // Hand the steps back to the QA Agent tab that opened this page.
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({ type: 'qa-record-steps', steps }, window.location.origin);
      setHandedOff(true);
      window.setTimeout(() => window.close(), 400);
    } else {
      // Opened directly: stash for the main tab to pick up.
      try { localStorage.setItem('qa_record_steps', JSON.stringify(steps)); } catch { /* ignore */ }
      setHandedOff(true);
    }
  }, [sessionId, steps]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense">
          <VideocamIcon sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ mr: 2 }}>Record &amp; generate</Typography>
          {phase === 'recording' && <Chip size="small" color="error" label="● recording" />}
          {phase === 'stopped' && <Chip size="small" color="default" label="stopped" />}
          {phase === 'ended' && <Chip size="small" color="warning" label="session ended" />}
          {phase === 'recording' && remaining !== null && (
            <Chip
              size="small"
              color={remaining <= 120 ? 'error' : remaining <= 300 ? 'warning' : 'default'}
              variant={remaining <= 300 ? 'filled' : 'outlined'}
              label={`session ends in ${fmt(remaining)}`}
              sx={{ ml: 1 }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {(phase === 'recording' || phase === 'stopped') && (
            <Typography variant="caption" color="text.secondary">{steps.length} steps captured</Typography>
          )}
        </Toolbar>
      </AppBar>

      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="warning" onClose={() => setNotice(null)}>{notice}</Alert>}
      {phase === 'ended' && (
        <Alert severity="warning">
          The browser session reached its time limit and ended. Your captured steps are
          safe — click <b>Generate test</b>{steps.length === 0 ? '' : ''}, or <b>Record again</b>.
        </Alert>
      )}
      {handedOff && (
        <Alert severity="success">
          Steps sent to the QA Agent — switch to that tab to see the test being generated.
          {!window.opener && ' (Reopen from the QA Agent to auto-generate.)'}
        </Alert>
      )}

      {phase === 'idle' && (
        <Box sx={{ m: 'auto', width: 560, maxWidth: '90%', textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>What flow do you want to record?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter where to start. Then drive the flow that matters — clicks and inputs are
            captured into a test. Passwords are never recorded.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField fullWidth autoFocus size="small" label="Start URL"
              placeholder="https://tms.qa-platform.local/login"
              value={startUrl} onChange={(e) => setStartUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleStart(); }} />
            <Button variant="contained" color="error" startIcon={<RecordIcon />}
              onClick={handleStart} disabled={!startUrl.trim()} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
              Start
            </Button>
          </Box>
        </Box>
      )}

      {phase === 'starting' && (
        <Box sx={{ m: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={22} /> <Typography>Provisioning the browser…</Typography>
        </Box>
      )}

      {(phase === 'recording' || phase === 'stopped' || phase === 'ended') && (
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Live browser — takes the whole remaining viewport */}
          <Box sx={{ flex: 1, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', p: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
              <TextField size="small" fullWidth value={startUrl}
                onChange={(e) => setStartUrl(e.target.value)}
                InputProps={{ sx: { fontSize: '0.8rem', fontFamily: 'monospace' } }} />
              <Button size="small" variant="outlined"
                startIcon={reloading ? <CircularProgress size={12} /> : <RefreshIcon />}
                onClick={handleReload} disabled={reloading || phase === 'ended'} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                Reload
              </Button>
            </Box>
            {phase === 'ended' ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">The browser session has ended (time limit reached).</Typography>
              </Box>
            ) : vncUrl
              ? <iframe title="live" src={vncUrl} style={{ width: '100%', flex: 1, border: 0 }} />
              : <Box sx={{ p: 2 }}><CircularProgress size={20} /></Box>}
          </Box>
          {/* Steps + actions */}
          <Box sx={{ width: 380, display: 'flex', flexDirection: 'column', p: 1.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Captured steps ({steps.length})</Typography>
            <Box sx={{ flex: 1, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
              {steps.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  Interact with the browser on the left — clicks and inputs appear here.
                </Typography>
              ) : steps.map((s, i) => (
                <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', py: 0.3 }}>
                  {i + 1}. {stepLabel(s)}
                </Typography>
              ))}
            </Box>
            {phase === 'stopped' && steps.length === 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                No steps were captured. Make sure the page is visible in the live view
                (Reload if it looks blank), then click on it and type — those actions
                are recorded. Then Record again.
              </Alert>
            )}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
              {phase === 'recording' ? (
                <Button fullWidth variant="contained" color="error" startIcon={<StopIcon />}
                  onClick={handleStop} sx={{ textTransform: 'none' }}>Stop</Button>
              ) : steps.length === 0 ? (
                <Button fullWidth variant="outlined" color="error" startIcon={<RecordIcon />}
                  onClick={recordAgain} sx={{ textTransform: 'none' }}>Record again</Button>
              ) : (
                <>
                  <Button variant="outlined" startIcon={<RecordIcon />}
                    onClick={recordAgain} sx={{ textTransform: 'none' }}>Redo</Button>
                  <Button fullWidth variant="contained" startIcon={<RunIcon />}
                    onClick={handleGenerate} disabled={handedOff} sx={{ textTransform: 'none' }}>
                    Generate test
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RecorderPage;
